import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCrown, FaArrowLeft, FaGem, FaStar, FaTrophy, FaBolt, FaFire, FaBox, FaGift, FaCalculator, FaExclamationTriangle, FaExternalLinkAlt, FaHandHoldingUsd } from 'react-icons/fa';
import LevelProgress from '../VIPLounge/LevelProgress';
import BenefitsList from '../VIPLounge/BenefitsList';
import { useCandyMachine } from '../../hooks/useCandyMachine';
import { getLevelName, getLevelTier } from '../../data/candyConfig';
import { useRadixConnect } from '../../../radix_connect';
import {
  RADIX_API_GATEWAY_URL,
  GBOF_RESOURCE_ADDRESS,
} from '../../../constants';
import './VIPLoungePage.css';

/**
 * VIPLoungePage - Premium leveling section where users deposit GBOF to level up
 * Higher levels unlock better odds in the Candy Shop
 */
const VIPLoungePage = () => {
  const navigate = useNavigate();
  const { selectedAccount, connected } = useRadixConnect();
  const [showLevelUp, setShowLevelUp] = useState(false);
  const previousLevelRef = useRef(null);

  const {
    level,
    depositedGbof,
    isLevelingUp,
    depositGbof,
    gbofNeededForNextLevel,
    maxLevel,
    getTotalGbofForLevel,
    getGbofRemainingForNextLevel
  } = useCandyMachine();

  // Real GBOF balance from Radix
  const [gbofBalance, setGbofBalance] = useState(0);

  // Fetch real GBOF balance from Radix Gateway API
  const fetchGbofBalance = useCallback(async () => {
    if (!selectedAccount?.address) {
      setGbofBalance(0);
      return;
    }

    try {
      let cursor = null;
      let found = false;
      let state_version = null;

      do {
        const requestBody = {
          address: selectedAccount.address,
        };
        if (cursor) {
          requestBody.cursor = cursor;
          requestBody.at_ledger_state = { state_version };
        }

        const response = await fetch(
          RADIX_API_GATEWAY_URL + "/state/entity/page/fungibles/",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) break;

        const data = await response.json();
        const items = data.items || [];

        for (const item of items) {
          if (item.resource_address === GBOF_RESOURCE_ADDRESS) {
            setGbofBalance(Math.floor(parseFloat(item.amount) || 0));
            found = true;
            break;
          }
        }

        cursor = data.next_cursor || null;
        state_version = data.ledger_state.state_version;
      } while (cursor && !found);

      if (!found) setGbofBalance(0);
    } catch (error) {
      console.error("Failed to fetch GBOF balance:", error);
      setGbofBalance(0);
    }
  }, [selectedAccount]);

  // Fetch GBOF balance on mount and when account changes
  useEffect(() => {
    if (connected && selectedAccount) {
      fetchGbofBalance();
    }
  }, [connected, selectedAccount, fetchGbofBalance]);

  // Handle single GBOF deposit
  const handleDepositGbof = useCallback(async () => {
    if (level >= maxLevel) return; // Max level — no more deposits
    if (gbofBalance >= 1) {
      await depositGbof(1);
      // Re-fetch real balance from chain after deposit
      fetchGbofBalance();
    }
  }, [gbofBalance, depositGbof, level, maxLevel, fetchGbofBalance]);

  // Navigate to Candy Shop
  const handleGoToShop = useCallback(() => {
    navigate('/dckslap/candyshop');
  }, [navigate]);

  // Level-up celebration effect - only triggers when level actually increases
  useEffect(() => {
    // Skip on initial load - just record the initial level
    if (previousLevelRef.current === null) {
      previousLevelRef.current = level;
      return;
    }

    // Only show celebration if the level increased from deposit
    if (level > previousLevelRef.current) {
      setShowLevelUp(true);
      const timer = setTimeout(() => setShowLevelUp(false), 2000);
      previousLevelRef.current = level;
      return () => clearTimeout(timer);
    }

    // Update ref if level changed for any other reason
    previousLevelRef.current = level;
  }, [level]);

  // Get tier info (adjusted for 0-11 scale, with icons)
  const getTierInfo = (lvl) => {
    const tier = getLevelTier(lvl);
    const icons = {
      1: <FaFire />,
      2: <FaStar />,
      3: <FaBolt />,
      4: <FaGem />,
      5: <FaCrown />,
    };
    return {
      ...tier,
      icon: icons[tier.tier] || <FaStar />,
    };
  };

  const currentTier = getTierInfo(level);

  // Calculate next tier by finding the next tier threshold
  // Tier thresholds for styling progression
  const getNextTierLevel = (currentLevel) => {
    const thresholds = [3, 5, 7, 11]; // Tier upgrade thresholds
    for (const threshold of thresholds) {
      if (currentLevel < threshold) return threshold;
    }
    return null; // Already at max tier
  };

  const nextTierLevel = getNextTierLevel(level);
  const nextTier = nextTierLevel !== null ? getTierInfo(nextTierLevel) : currentTier;

  return (
    <div className={`vip-lounge-page ${showLevelUp ? 'celebrating' : ''}`}>
      {/* Level Up Celebration Overlay */}
      {showLevelUp && (
        <div className="level-up-celebration">
          <div className="celebration-content">
            <FaCrown className="celebration-icon" />
            <div className="celebration-text">LEVEL UP!</div>
            <div className="celebration-level">Level {level}</div>
            <div className="celebration-name">{getLevelName(level)}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="vip-header">
        <div className="header-left">
          <div className="vip-title">
            <FaCrown className="vip-icon" />
            <h1>VIP Lounge</h1>
          </div>
          <p className="vip-subtitle">
            Level up to unlock better rewards in the Candy Shop
          </p>
        </div>
        <button className="shop-link" onClick={handleGoToShop}>
          <FaBox />
          <span>Go to Candy Shop</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="vip-content">
        {/* Left Column - Level Progress */}
        <div className="vip-main">
          <LevelProgress
            level={level}
            depositedGbof={depositedGbof}
            maxLevel={maxLevel}
            onDepositGbof={handleDepositGbof}
            isLevelingUp={isLevelingUp}
            gbofBalance={gbofBalance}
            gbofNeededForNextLevel={gbofNeededForNextLevel}
          />

          {/* Fee Warning */}
          <div className="fee-warning-card">
            <FaExclamationTriangle className="warning-icon" />
            <div className="warning-text">
              <strong>XRD Protocol Fee</strong> applies per GBOF deposit
            </div>
          </div>

          {/* GBOF Info Card - How to earn */}
          <div className="gbof-info-card">
            <div className="gbof-header">
              <FaFire className="gbof-icon" />
              <span>How to Earn GBOF</span>
            </div>
            <div className="gbof-content">
              <p className="gbof-intro">
                GBOF (Great Balls Of Fire) tokens unlock powerful level-ups.
                Here's how to get them:
              </p>

              <div className="gbof-methods">
                {/* Method 1: Deposits */}
                <div className="gbof-method-card">
                  <div className="method-icon-wrapper deposit">
                    <FaBox className="method-icon" />
                  </div>
                  <div className="method-content">
                    <h4>Deposit Milestones</h4>
                    <p>Earn <strong>1 GBOF</strong> for every <strong>300 deposits</strong> in the DCKSLAP game</p>
                  </div>
                </div>

                {/* Method 2: Claiming */}
                <div className="gbof-method-card">
                  <div className="method-icon-wrapper claim">
                    <FaHandHoldingUsd className="method-icon" />
                  </div>
                  <div className="method-content">
                    <h4>Claim Rewards</h4>
                    <p>Receive GBOF tokens when you <strong>claim</strong> your earned rewards</p>
                  </div>
                </div>

                {/* Method 3: Buy on DEX */}
                <div className="gbof-method-card">
                  <div className="method-icon-wrapper buy">
                    <FaGift className="method-icon" />
                  </div>
                  <div className="method-content">
                    <h4>Trade on DEX</h4>
                    <p>Buy GBOF directly on Radix DEXes</p>
                    <div className="dex-links">
                      <a
                        href="https://ociswap.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dex-link"
                      >
                        <span>Ociswap</span>
                        <FaExternalLinkAlt />
                      </a>
                      <a
                        href="https://caviarnine.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dex-link"
                      >
                        <span>CaviARNine</span>
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Benefits & Tiers */}
        <div className="vip-sidebar">
          {/* Current Tier Card */}
          <div className="tier-card" style={{ borderColor: currentTier.color }}>
            <div
              className="tier-glow"
              style={{ background: `radial-gradient(circle, ${currentTier.color}30 0%, transparent 70%)` }}
            />
            <div className="tier-header">
              <span className="tier-label">Current Level</span>
              <div className="tier-name" style={{ color: currentTier.color }}>
                {currentTier.icon}
                <span>Level {level}</span>
              </div>
            </div>
            <p className="tier-desc level-name-display">{getLevelName(level)}</p>

            {nextTierLevel !== null && (
              <div className="next-tier">
                <span className="next-label">Next:</span>
                <span className="next-name" style={{ color: nextTier.color }}>
                  Level {nextTierLevel}
                </span>
                <span className="next-level">{getLevelName(nextTierLevel)}</span>
              </div>
            )}
          </div>

          {/* Level Cost Calculator - Linear progression */}
          <div className="cost-calculator">
            <h3 className="calc-title">
              <FaCalculator />
              <span>Level Cost Calculator</span>
            </h3>
            <p className="calc-note">
              Cost to reach a level = Target Level GBOF
            </p>
            <div className="calc-grid">
              {[1, 3, 5, 7, 11].map((targetLevel) => (
                <div
                  key={targetLevel}
                  className={`calc-item ${level >= targetLevel ? 'achieved' : ''}`}
                >
                  <span className="calc-level">LVL {targetLevel}</span>
                  <span className="calc-cost">
                    {targetLevel} GBOF to reach
                  </span>
                  <span className="calc-tier" style={{ color: getTierInfo(targetLevel).color }}>
                    {getLevelName(targetLevel)}
                  </span>
                </div>
              ))}
            </div>
            <div className="calc-formula">
              <span>Total GBOF for Level N = N GBOF</span>
            </div>
          </div>



          {/* Benefits List */}
          <BenefitsList currentLevel={level} />
        </div>
      </div>

      {/* Back Link */}
      <button className="back-link" onClick={() => navigate('/dckslap/candyshop')}>
        <FaArrowLeft />
        <span>Back to Candy Shop</span>
      </button>
    </div>
  );
};

export default VIPLoungePage;
